import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X, AlertTriangle } from "lucide-react";

type Status = "done" | "todo" | "warn";

const items: { status: Status; label: string; detail: string }[] = [
  { status: "done", label: "Account deletion route", detail: "/settings/delete-account live" },
  { status: "done", label: "Legal pages", detail: "Privacy, terms, refunds, acceptable use, escrow, disputes" },
  { status: "done", label: "Android AAB workflow", detail: ".github/workflows/android-aab.yml" },
  { status: "done", label: "iOS release workflow", detail: ".github/workflows/ios-release.yml" },
  { status: "done", label: "Sentry wiring", detail: "Set VITE_SENTRY_DSN to enable" },
  { status: "done", label: "Deep link manifests", detail: "/.well-known/assetlinks.json + apple-app-site-association" },
  { status: "done", label: "Push notification scaffold", detail: "src/lib/push.ts registers after login on native" },
  { status: "warn", label: "Rename Capacitor appId", detail: "Currently app.lovable.* — must become app.cashisle.{android,ios}" },
  { status: "warn", label: "Replace asset-link placeholders", detail: "SHA-256 fingerprint + Apple TEAMID" },
  { status: "todo", label: "Google Play Console account", detail: "$25 one-time, 1–2d verification" },
  { status: "todo", label: "Apple Developer Program", detail: "$99/yr, 24–48h approval" },
  { status: "todo", label: "Signing material", detail: "Android keystore + iOS .p12 + provisioning profile" },
  { status: "todo", label: "Play service account JSON", detail: "Release manager role" },
  { status: "todo", label: "Store assets", detail: "Icon 1024², feature graphic, screenshots 6.7\"+1080p" },
  { status: "todo", label: "Privacy nutrition labels", detail: "Fill App Store Connect form per STORE_LISTING.md §4" },
  { status: "todo", label: "Custom domain", detail: "cashisle.app for legal/support URLs" },
  { status: "todo", label: "Support inboxes live", detail: "support@ and privacy@ cashisle.app" },
  { status: "todo", label: "Demo reviewer account", detail: "review@cashisle.app, $250 balance, KYC pre-approved" },
  { status: "todo", label: "Sumsub production keys", detail: "Flip sandbox → production secrets" },
  { status: "todo", label: "Lovable Cloud paid tier", detail: "Free tier pauses → app outage risk" },
  { status: "todo", label: "Internal track smoke test", detail: "Sign-in, send, receive, KYC, gig end-to-end" },
];

const Icon = ({ status }: { status: Status }) => {
  if (status === "done") return <Check className="h-5 w-5 text-primary" />;
  if (status === "warn") return <AlertTriangle className="h-5 w-5 text-destructive" />;
  return <X className="h-5 w-5 text-muted-foreground" />;
};

const LaunchReadiness = () => {
  const done = items.filter((i) => i.status === "done").length;
  return (
    <div className="container mx-auto max-w-3xl p-4 space-y-4">
      <Helmet>
        <title>Launch Readiness | Cash Isle</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Card>
        <CardHeader>
          <CardTitle>Launch readiness</CardTitle>
          <p className="text-sm text-muted-foreground">
            {done} / {items.length} complete
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {items.map((i) => (
            <div key={i.label} className="flex gap-3 items-start">
              <Icon status={i.status} />
              <div>
                <div className="font-medium">{i.label}</div>
                <div className="text-sm text-muted-foreground">{i.detail}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default LaunchReadiness;
